import { useState, useEffect, useCallback, useRef } from 'react';
import { InteractionManager } from 'react-native';
import { supabase } from '../lib/supabase';
import { RealtimeChannel } from '@supabase/supabase-js';

export interface Order {
  id: string;
  created_at: string;
  status: string;
  total_amount: number;
  items: Array<{ name: string; quantity: number }>;
  payment_method: string | null;
  payment_details: any;
  payment_intent_id?: string | null;
  delivery_id?: string | null;
  delivery_status?: string | null;   // keep for forward compat
  tracking_url?: string | null;
}

export function useOrders() {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [userId, setUserId] = useState<string | null>(null);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  
  // Prevent duplicate concurrent fetches
  const isFetchingRef = useRef(false);

  // Listen for auth changes to update userId
  useEffect(() => {
    const fetchUser = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      setUserId(session?.user?.id ?? null);
    };
    fetchUser();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUserId(session?.user?.id ?? null);
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const fetchOrders = useCallback(async () => {
    if (!userId) {
      // No user logged in - only clear orders on initial load, not on refetch
      // This prevents flash when userId briefly becomes null during focus
      if (!hasLoadedOnce) {
        setOrders([]);
        setError(null);
        setLoading(false);
      }
      return;
    }
    
    // Prevent duplicate concurrent fetches
    if (isFetchingRef.current) {
      return;
    }
    isFetchingRef.current = true;

    try {
      // Only show loading state on initial load, not on refetch
      // This prevents layout shift when navigating back to the screen
      if (!hasLoadedOnce) {
        setLoading(true);
      }
      setError(null);

      const { data, error: qErr } = await supabase
        .from('orders')
        .select(
          `id, created_at, status, total_amount, items, payment_method, payment_details, payment_intent_id, delivery_id, tracking_url`
        )
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (qErr) throw qErr;

      // If you later want delivery_status on the card, store it on `orders` too,
      // or fetch from deliveries in a second call.
      setOrders((data ?? []) as Order[]);
      setHasLoadedOnce(true);
    } catch (err: any) {
      // expose the real Supabase message if present
      const msg =
        typeof err?.message === 'string'
          ? err.message
          : (err?.details || err?.hint || 'An error occurred while fetching orders');
      setError(msg);
      console.error('useOrders error →', err);
    } finally {
      setLoading(false);
      isFetchingRef.current = false;
    }
  }, [userId, hasLoadedOnce]);

  // Real-time subscription setup
  const setupRealtimeSubscription = useCallback(() => {
    let channel: RealtimeChannel | null = null;

    const initSubscription = async () => {
      if (!userId) return;

      channel = supabase
        .channel(`orders-${userId}`)
        .on(
          'postgres_changes',
          {
            event: 'UPDATE',
            schema: 'public',
            table: 'orders',
            filter: `user_id=eq.${userId}`,
          },
          (payload) => {
            console.log('📦 Order status updated:', payload.new?.id);
            const updatedOrder = payload.new as Order;
            
            setOrders(prevOrders => 
              prevOrders.map(order => 
                order.id === updatedOrder.id 
                  ? { ...order, ...updatedOrder }
                  : order
              )
            );
          }
        )
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'orders',
            filter: `user_id=eq.${userId}`,
          },
          (payload) => {
            console.log('📦 New order created:', payload.new?.id);
            const newOrder = payload.new as Order;
            
            setOrders(prevOrders => [newOrder, ...prevOrders]);
          }
        )
        .on(
          'postgres_changes',
          {
            event: 'DELETE',
            schema: 'public',
            table: 'orders',
            filter: `user_id=eq.${userId}`,
          },
          (payload) => {
            console.log('📦 Order deleted:', payload);
            const deletedOrderId = payload.old?.id;
            
            if (deletedOrderId) {
              setOrders(prevOrders => 
                prevOrders.filter(order => order.id !== deletedOrderId)
              );
            }
          }
        )
        .subscribe((status) => {
          console.log('Orders subscription status:', status);
        });
    };

    initSubscription();

    return () => {
      if (channel) {
        supabase.removeChannel(channel);
      }
    };
  }, [userId]);

  // Use InteractionManager to defer fetch until animations complete
  // This prevents blocking touches during navigation
  useEffect(() => {
    const task = InteractionManager.runAfterInteractions(() => {
      fetchOrders();
    });
    return () => task.cancel();
  }, [fetchOrders]);

  // Use InteractionManager for subscription setup too
  useEffect(() => {
    if (userId) {
      const task = InteractionManager.runAfterInteractions(() => {
        const cleanup = setupRealtimeSubscription();
        task.cancel = () => {
          if (cleanup) cleanup();
        };
      });
      return () => task.cancel();
    }
  }, [setupRealtimeSubscription, userId]);

  return { orders, loading, error, refetch: fetchOrders };
}